import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import Header from './Header'
import Sidebar from './Sidebar'
import MapContainer from '../map/MapContainer'
import { useCoffeeShops, Bounds, CoffeeShop } from '../../hooks/useCoffeeShops'
import { useDebounce } from '../../hooks/useDebounce'
import { VersionDisplay } from '../../utils/version'

import { ViewState, LayerConfig, VisualizationType } from '../../types/map'

const INITIAL_VIEW_STATE: ViewState = {
  longitude: -75.1652,
  latitude: 39.9526,
  zoom: 12,
  pitch: 0,
  bearing: 0
}

const LAYER_CONFIG: LayerConfig[] = [
  { name: 'Coffee Shops', enabled: true, color: 'coffee' },
  { name: 'Weather', enabled: false, color: 'blue' }
]

function getBounds(viewState: ViewState): Bounds {
  // Input validation
  if (!viewState || typeof viewState !== 'object') {
    console.warn('getBounds: viewState must be an object, using default bounds')
    return {
      south: 39.8,
      west: -75.3,
      north: 40.1,
      east: -75.0,
    }
  }

  const { longitude, latitude, zoom } = viewState

  // Validate reasonable ranges
  if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90 || zoom < 0 || zoom > 24) {
    console.warn('getBounds: viewState contains out-of-range values, using default bounds')
    return {
      south: 39.8,
      west: -75.3,
      north: 40.1,
      east: -75.0,
    }
  }

  // Calculate bounds manually since getBounds() is not available in community typings
  // This is a simplified calculation based on zoom level and viewport dimensions
  // Increased minimum delta to reduce API calls and improved scaling
  const delta = Math.max(0.05, 1.0 / Math.pow(2, zoom - 6))
  if (import.meta.env.DEV) {
    console.log('Using bounds calculation with delta:', delta)
  }
  return {
    south: latitude - delta,
    west: longitude - delta,
    north: latitude + delta,
    east: longitude + delta,
  }
}

const Layout: React.FC = () => {
  const [viewState, setViewState] = useState<ViewState>(INITIAL_VIEW_STATE)
  const [searchQuery, setSearchQuery] = useState('')
  const [layers, setLayers] = useState<LayerConfig[]>(LAYER_CONFIG)
  const [visualizationType, setVisualizationType] = useState<VisualizationType>('scatter')
  const [heatmapIntensity] = useState<number>(2)
  const [isDragging, setIsDragging] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)

  // Set sidebar to closed by default on mobile
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 1024 // lg breakpoint
      setIsSidebarOpen(!isMobile)
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  // Debounce viewState changes to prevent excessive API calls during dragging
  const debouncedViewState = useDebounce(viewState, isDragging ? 500 : 100)

  // Track significant bounds changes to avoid unnecessary API calls
  const lastBoundsRef = useRef<Bounds | null>(null)

  const bounds = useMemo(() => {
    const calculatedBounds = getBounds(debouncedViewState)

    // Check if bounds have changed significantly
    if (lastBoundsRef.current) {
      const lastBounds = lastBoundsRef.current
      const deltaNorth = Math.abs(calculatedBounds.north - lastBounds.north)
      const deltaSouth = Math.abs(calculatedBounds.south - lastBounds.south)
      const deltaEast = Math.abs(calculatedBounds.east - lastBounds.east)
      const deltaWest = Math.abs(calculatedBounds.west - lastBounds.west)

      // Only update if the change is significant (prevents micro-movements from triggering API calls)
      if (deltaNorth < 0.01 && deltaSouth < 0.01 && deltaEast < 0.01 && deltaWest < 0.01) {
        return lastBounds
      }
    }

    lastBoundsRef.current = calculatedBounds
    return calculatedBounds
  }, [debouncedViewState])

  const { data: coffeeShops = [], isLoading } = useCoffeeShops(bounds)

  // Filter coffee shops based on search query
  const filteredCoffeeShops = useMemo(() => {
    if (!searchQuery.trim()) {
      return coffeeShops
    }
    
    const query = searchQuery.toLowerCase().trim()
    return coffeeShops.filter((shop: CoffeeShop) => {
      return (
        (shop.name && shop.name.toLowerCase().includes(query)) ||
        (shop.brand && shop.brand.toLowerCase().includes(query)) ||
        (shop.city && shop.city.toLowerCase().includes(query)) ||
        (shop.cuisine && shop.cuisine.toLowerCase().includes(query))
      )
    })
  }, [coffeeShops, searchQuery])

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
  }

  const handleVisualizationTypeChange = (type: VisualizationType) => {
    setVisualizationType(type)
  }

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  // Handle drag state changes from MapView
  const handleDragStateChange = useCallback((dragging: boolean) => {
    setIsDragging(dragging)
  }, [])

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <Header 
        onSearchChange={handleSearchChange} 
        onSidebarToggle={handleSidebarToggle}
        isSidebarOpen={isSidebarOpen}
      />
      
      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile Backdrop */}
        {isSidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={handleSidebarToggle}
          />
        )}
        
        {/* Sidebar */}
        <div className={`sidebar-container ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <Sidebar
            coffeeShopCount={filteredCoffeeShops.length}
            isLoading={isLoading}
            layers={layers}
            setLayers={setLayers}
          />
        </div>
        
        {/* Map Container */}
        <div className="flex-1 relative">
          <MapContainer
            viewState={viewState}
            setViewState={setViewState}
            coffeeShops={filteredCoffeeShops}
            layers={layers}
            setLayers={setLayers}
            visualizationType={visualizationType}
            heatmapIntensity={heatmapIntensity}
            onDragStateChange={handleDragStateChange}
            onVisualizationTypeChange={handleVisualizationTypeChange}
          />
        </div>
      </div>
      
      {/* Version Display */}
      <VersionDisplay />
    </div>
  )
}

export default Layout 